import React, { useMemo, useState } from "react";
import { Pie, Bar, Doughnut, Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  PointElement,
  LineElement,
  Filler,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { Accordion, Card } from "react-bootstrap";
import { CircleMinus, CirclePlus } from "lucide-react";
import { BarChart, LineChart, PieChart, TorusIcon } from "lucide-react";
import { PeriodType } from "../EmpUIHelper";
import { useSelector } from "react-redux";

ChartJS.register(ArcElement, Tooltip, Legend);
ChartJS.register(ChartDataLabels);
ChartJS.register(PointElement, LineElement, Filler);

// Optimized, non-repeating color palette
const colorPalette = [
  "#3ec9a7",
  "#2d2e83",
  "#6c3fa7",
  "#f45c2c",
  "#3db6e9",
  "#f0ad4e",
  "#d9534f",
  "#5bc0de",
  "#5cb85c",
];

const getOptions = (chartType, chartId) => {
  const showGrid =
    chartType === "bar" ||
    chartType === "line" ||
    chartType === "horizontalBar";

  // Function to truncate labels to 10 characters
  const truncateLabel = (label) => {
    if (typeof label === "string" && label.length > 10) {
      return label.substring(0, 10) + "...";
    }
    return label;
  };

  return {
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: false,
        external: function(context) {
          const uniqueId = `chartjs-tooltip-${chartId}`;
          let tooltipEl = document.getElementById(uniqueId);

          if (!tooltipEl) {
            tooltipEl = document.createElement("div");
            tooltipEl.id = uniqueId;
            tooltipEl.style.position = "absolute";
            tooltipEl.style.background = "#E6F8FF";
            tooltipEl.style.color = "#0093DD";
            tooltipEl.style.fontSize = "10.4px";
            tooltipEl.style.fontWeight = "500";
            tooltipEl.style.padding = "4px 8px";
            tooltipEl.style.borderRadius = "6px";
            tooltipEl.style.pointerEvents = "none";
            tooltipEl.style.whiteSpace = "nowrap";
            tooltipEl.style.transition = "all .1s ease";
            tooltipEl.style.zIndex = "9999";
            document.body.appendChild(tooltipEl);
          }

          const tooltipModel = context.tooltip;

          if (tooltipModel.opacity === 0) {
            tooltipEl.style.opacity = 0;
            return;
          }

          if (tooltipModel.dataPoints?.length) {
            const dp = tooltipModel.dataPoints[0];
            let value = dp.raw;
            if (typeof value === "object" && value !== null) {
              const isHorizontal = context.chart.options.indexAxis === "y";
              value = isHorizontal ? value.x : value.y;
            }

            const dataArr = dp.dataset.data.map((v) =>
              typeof v === "object" && v !== null
                ? typeof v.x === "number"
                  ? v.x
                  : v.y
                : v,
            );
            // Convert all values to number
            const total = dataArr.reduce((a, b) => a + Number(b), 0);
            const numericValue = Number(value);
            const percent = total
              ? ((numericValue / total) * 100).toFixed(1)
              : 0;

            tooltipEl.innerHTML = `${dp.label}: ${numericValue}${
              percent ? ` (${percent}%)` : ""
            }`;
          }

          const canvasRect = context.chart.canvas.getBoundingClientRect();
          const { caretX, caretY } = tooltipModel;

          tooltipEl.style.opacity = 1;
          tooltipEl.style.left =
            canvasRect.left + window.scrollX + caretX + "px";
          tooltipEl.style.top = canvasRect.top + window.scrollY + caretY + "px";
        },
      },
      datalabels: {
        display: false,
      },
    },
    scales: {
      x: {
        ticks: {
          display: showGrid,
          callback: function(value) {
            // For horizontal bar chart, x-axis shows the values (numbers), no truncation needed
            if (chartType === "horizontalBar") {
              return value;
            }
            // For vertical bar and line charts, x-axis shows labels - truncate them
            const label = this.getLabelForValue(value);
            return truncateLabel(label);
          },
          font: {
            size: 9, //  change font size for X-axis
          },
          maxRotation: 20, // Prevent rotation
          autoSkip: false, // Ensure all labels are shown (they'll be truncated)
        },
        grid: {
          display: showGrid,
          drawBorder: false,
        },
      },
      y: {
        ticks: {
          display: showGrid,
          callback: function(value) {
            // For horizontal bar chart, y-axis shows labels - truncate them
            if (chartType === "horizontalBar") {
              const label = this.getLabelForValue(value);
              return truncateLabel(label);
            }
            // For vertical bar and line charts, y-axis shows values (numbers)
            return value;
          },
          font: {
            size: 9, //  change font size for X-axis
          },
          maxRotation: 20,
          autoSkip: true,
        },
        grid: {
          display: showGrid,
          drawBorder: false,
        },
      },
    },
  };
};
const ChartData = ({ data, expand ,setModalData }) => {
  const [chartType, setChartType] = useState(data?.graph_type || "pie");
  const [activeKey, setActiveKey] = useState(expand ? "0" : null); //
  const dashboard = useSelector((state) => state.dashboard || []);

const handleChartClick = (event, elements) => {
  if (!elements?.length) return;

  const index = elements[0].index;

  // jis chart item pe click hua
  const clickedItem = data?.data?.[index];

  console.log("Clicked Item:", clickedItem);

  if (clickedItem?.details?.length) {
    setModalData({
      data: clickedItem.details,
      dashlet_name: data?.dashlet_name || "Details",
    });
  }
};
  // Memoize departmentData and total
  const departmentData = useMemo(() => {
    const labels =
      data?.data?.length > 0 ? data.data.map((item) => item.label) : [];
    const values =
      data?.data?.length > 0 ? data.data.map((item) => item.total) : [];
    return {
      labels,
      datasets: [
        {
          data: values,
          backgroundColor: values.map(
            (_, idx) => colorPalette[idx % colorPalette.length],
          ),
          fill: true,
        },
      ],
    };
  }, [data]);
  const chartTypes = [
    {
      key: "pie",
      label: "Pie",
      icon: <PieChart className="w-3 h-3 stroke-3 " />,
    },
    {
      key: "bar",
      label: "Bar",
      icon: <BarChart className="w-3 h-3 stroke-3 " />,
    },
    {
      key: "doughnut",
      label: "Doughnut",
      icon: <TorusIcon className="w-3 h-3 stroke-3 " />,
    },
    {
      key: "line",
      label: "Line",
      icon: <LineChart className="w-3 h-3 stroke-3 " />,
    },
    {
      key: "horizontalBar",
      label: "Horizontal Bar",
      icon: <BarChart className="w-3 h-3 stroke-3  rotate-90" />,
    },
  ];

  const getHeading = () => {
    if (!data) return "";

    let name = data.dashlet_name || "";

    // Handle period type name override
    if (data.isPeriodType) {
      const periodLabel = PeriodType[data.PeriodType];

      if (periodLabel) {
        name = `${name} In ${periodLabel}`;
      } else {
        const type = data.sequenceStatus == "0" ? "Next" : "Last";
        name = `${name} In ${type} ${data.days} Days`;
      }
    }

    // Add trend label
    if (data.trendValue) {
      const label =
        data.trendValue == 1
          ? dashboard?.activeFiscalYear?.label
          : dashboard?.activeFiscalYear?.payrollMonthLabel;

      name = `${name} - ${label ?? ""}`;
    }

    return name;
  };

  return (
    <div className="bg-white rounded-lg px-3 py-2">
      <Accordion activeKey={activeKey} onSelect={(key) => setActiveKey(key)}>
        <Card className="border-0">
          <Card.Header className="bg-white border-0 p-0">
            <div className="flex justify-between items-center w-full">
              {/* Left side - Accordion toggle with icon and heading */}
              <Accordion.Toggle
                eventKey="0"
                className="flex items-center gap-2 p-0 border-0 bg-transparent shadow-none hover:bg-transparent"
                style={{ outline: "none" }}
              >
                {activeKey === "0" ? (
                  <CircleMinus className="h-4 w-4 text-tprimary" />
                ) : (
                  <CirclePlus className="h-4 w-4 text-tprimary" />
                )}
                <h6 className="font-bold mb-0 text-tprimary">{getHeading()}</h6>
              </Accordion.Toggle>

              {/* Right side - Chart type selection buttons (only visible when accordion is open) */}
              {activeKey === "0" && (
                <div className="flex space-x-1">
                  {chartTypes.map((type) => (
                    <button
                      key={type.key}
                      type="button"
                      onClick={() => setChartType(type.key)}
                      className={`text-xs border-none rounded-sm w-6 h-6  ${
                        type.key === chartType
                          ? "text-tprimary bg-tsecondary"
                          : "text-black bg-tlight"
                      }`}
                    >
                      {type.icon}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </Card.Header>
          <Accordion.Collapse eventKey="0">
            <div className="relative max-h-[23rem] overflow-auto scrollbar-fancy mt-1">
              <div
                style={{
                  width:
                    chartType === "pie" || chartType === "doughnut"
                      ? "180px"
                      : "100%",
                  height:
                    chartType === "pie" || chartType === "doughnut"
                      ? "180px"
                      : "auto",
                  display:
                    chartType !== "pie" || chartType !== "doughnut"
                      ? "flex"
                      : "",
                  alignItems:
                    chartType !== "pie" || chartType !== "doughnut"
                      ? "end"
                      : "",
                  position: "relative",
                  margin:
                    chartType === "pie" || chartType === "doughnut"
                      ? "0px auto"
                      : "0px",
                }}
                className="mb-2"
              >
                {data?.data?.length === 0 && (
                  <label className="text-center">No Record Available</label>
                )}
                {chartType === "pie" && (
                  // <Pie
                  //   data={departmentData}
                  //   options={getOptions(chartType, data?.Id)}
                  //   plugins={[ChartDataLabels]}
                  // />
                  <Pie
  data={departmentData}
  options={{
    ...getOptions(chartType, data?.Id),
    onClick: handleChartClick,
    onHover: (event, elements) => {
      event.native.target.style.cursor =
        elements?.length ? "pointer" : "default";
    },
  }}
  plugins={[ChartDataLabels]}
/>
                )}
                {chartType === "bar" && (
                  // <Bar
                  //   data={departmentData}
                  //   options={getOptions(chartType, data?.Id)}
                  //   plugins={[ChartDataLabels]}
                  // />
                  <Bar
  data={departmentData}
  options={{
    ...getOptions(chartType, data?.Id),
    onClick: handleChartClick,
    onHover: (event, elements) => {
      event.native.target.style.cursor =
        elements?.length ? "pointer" : "default";
    },
  }}
  plugins={[ChartDataLabels]}
/>
                )}
                {chartType === "horizontalBar" && (
                  // <Bar
                  //   data={departmentData}
                  //   options={{
                  //     ...getOptions(chartType, data?.Id),
                  //     indexAxis: "y",
                  //   }}
                  //   plugins={[ChartDataLabels]}
                  // />

                  <Bar
  data={departmentData}
  options={{
    ...getOptions(chartType, data?.Id),
    indexAxis: "y",
    onClick: handleChartClick,
    onHover: (event, elements) => {
      event.native.target.style.cursor =
        elements?.length ? "pointer" : "default";
    },
  }}
  plugins={[ChartDataLabels]}
/>
                )}
                {chartType === "doughnut" && (
                  // <Doughnut
                  //   data={departmentData}
                  //   options={getOptions(chartType, data?.Id)}
                  //   plugins={[ChartDataLabels]}
                  // />
                  <Doughnut
  data={departmentData}
  options={{
    ...getOptions(chartType, data?.Id),
    onClick: handleChartClick,
    onHover: (event, elements) => {
      event.native.target.style.cursor =
        elements?.length ? "pointer" : "default";
    },
  }}
  plugins={[ChartDataLabels]}
/>
                )}
                {chartType === "line" && (
                  // <Line
                  //   data={departmentData}
                  //   options={getOptions(chartType, data?.Id)}
                  //   plugins={[ChartDataLabels]}
                  // />

                  <Line
  data={departmentData}
  options={{
    ...getOptions(chartType, data?.Id),
    onClick: handleChartClick,
    onHover: (event, elements) => {
      event.native.target.style.cursor =
        elements?.length ? "pointer" : "default";
    },
  }}
  plugins={[ChartDataLabels]}
/>
                )}
              </div>
              <hr />
              <div className="grid grid-cols-4 gap-x-2 gap-y-1">
                {data?.data?.length > 0 &&
                  data?.data?.map((e, idx) => (
                    <div
                      key={idx}
                      className="grid grid-cols-5 gap-x-2 gap-y-1 items-center text-xs"
                    >
                      {/* <label
                  style={{
                    color: departmentData.datasets[0].backgroundColor[idx],
                  }}
                  className="w-2 h-2 rounded-full"
                /> */}
                      <p
                        style={{
                          color:
                            departmentData.datasets[0].backgroundColor[idx],
                        }}
                        className="text-black m-0 p-0 col-span-3 truncate"
                        title={e?.label}
                      >
                        {e?.label}
                      </p>
                      <p
                        style={{
                          color:
                            departmentData.datasets[0].backgroundColor[idx],
                        }}
                        className="font-extrabold m-0 col-span-2 text-right"
                      >
                        {e?.total ? Number(e?.total)?.toLocaleString() : 0}
                      </p>
                    </div>
                  ))}
              </div>
            </div>
          </Accordion.Collapse>
        </Card>
      </Accordion>
    </div>
  );
};

export default ChartData;
